import Auth0Lock from "auth0-lock";
import { AUTH_CONFIG } from "Common/pages/login/variables";

/*
User sign-up and login is an orchestrated dance between Auth0 and Ampd.
For a user to log into Ampd, they must have accounts in both systems.
Auth0 provides the identity attestation, while Ampd holds the user
metadata and configuration.

It is technically possible for us to create an Auth0 user through Ampd,
but it is preferable for the user to directly create their own Auth0 account,
as we are currently deferring email validation and ToS/privacy policy consent
to Auth0.

The normal flow works as follows:

1. The user visits https://app.ampd.io/login and selects the "Sign Up" tab.

2. The user checks the "I agree to the ToS..." checkbox, and then either logs in
   with Google or enters a username and password.

3. If the user creates an account via Google, their email_verified flag is set
   to true, since Google verifies that on behalf of Auth0. If they create an
   account with a user name and password, email_verified is set to false, and
   the user is directed to check their email for a verification link
   (managed in Auth0 through the "Emails" UI).

   NOTE: For all Auth0 users that we created through the old invitation
   workflow, we always set email_verified to true, since accepting the
   invitation would implicitly verify the email.

5. The final gate is another Auth0 rule called "Force email validation" that
   requires email_verified to be set to true before a user can log in through
   Auth0.

Though the email and rules behavior is managed through the Auth0 UI directly,
some of the behavior is configured in the widget and has been annotated below.
 */

const lockConfigOptions = (initialScreen, email) => {
  const baseConfig = {
    container: "auth0-container",
    allowedConnections: ["google-oauth2"],

    initialScreen,

    prefill: {
      email
    },

    // Allows the user to sign up from the Auth0 widget.
    allowSignUp: true,

    // Prevents users that sign up with a username and password from automatically
    // being logged in to the service.
    //
    // If this was true, they would end up getting a "Please validate your email"
    // error. When this is false, they instead get a more pleasant dialog asking
    // them to check their email for the validation link.
    loginAfterSignUp: false,

    // Requires the user to click the "I agree to the ToS..." checkbox before
    // they can sign up.
    mustAcceptTerms: true,

    theme: {
      primaryColor: "#F93822"
    },

    // These override the default strings that are shown in the Auth0 widget UI.
    languageDictionary: {
      signUpTerms:
        "I agree to the <a href='https://www.ampd.io/terms' target='_blank' rel='noopener noreferrer'>terms of service</a> and <a href='https://www.ampd.io/privacy' target='_blank' rel='noopener noreferrer'>privacy policy</a>.",
      success: {
        // This is shown if a user signs up with a username and password.
        signUp:
          "Welcome to Ampd! Please check your email for a verification link."
      },
      title: ""
    },

    auth: {
      audience: `https://${AUTH_CONFIG.domain}/userinfo`,
      responseType: "token id_token",
      params: {
        scope: "openid email"
      }
    }
  };

  // allow username/password auth in production only if this query param is set
  const urlParams = new URLSearchParams(window.location.search);
  const allowPasswordLogin = urlParams.get("basic-auth");
  if (
    allowPasswordLogin === "true" ||
    process.env.REACT_APP_ENV === "dev" ||
    process.env.REACT_APP_ENV === "staging"
  ) {
    baseConfig.allowedConnections.push("Username-Password-Authentication");
  }

  return baseConfig;
};

export const configureLock = ({ initialScreen, email }) => {
  return new Auth0Lock(
    AUTH_CONFIG.clientId,
    AUTH_CONFIG.domain,
    lockConfigOptions(initialScreen, email)
  );
};
